import * as amplitude from "@amplitude/analytics-browser";
import { jwtDecode } from "jwt-decode";

import { getCookieBotConsentStatistics } from "./cookieConsent";
import { addUserAttributesToDatadogRUM } from "./datadog";

/**
 * Validates that a value is a non-empty string with a structurally valid JWT format.
 * A JWT must consist of exactly three base64url-encoded segments separated by dots.
 */
export function isValidJwt(token: unknown): token is string {
  if (typeof token !== "string" || !token.trim()) return false;
  const jwtPattern = /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/;
  return jwtPattern.test(token);
}

/**
 * Parse an access token into specific data and return the expiration.
 * Also verifies that the user identity is correct for associated services.
 */
export function decodeAccessTokenAndAssignIdentity(accessToken: string): {
  sub: string;
  exp: number;
  user_name: string;
  expiration: Date;
} {
  // Validate before writing to storage — never store untrusted or malformed tokens.
  if (!isValidJwt(accessToken)) {
    throw new Error("Invalid access token.");
  }

  const decoded = jwtDecode<{ sub: string; exp: number; user_name: string }>(
    accessToken
  );
  const expiration = decoded?.exp ? new Date(decoded.exp * 1000) : new Date(0);

  if (getCookieBotConsentStatistics()) {
    addUserAttributesToDatadogRUM(decoded.sub, decoded.user_name);
    amplitude.setUserId(decoded.sub);
    const identity = new amplitude.Identify();
    identity.set("email", decoded.user_name);
    identity.set("id", decoded.sub);
    amplitude.identify(identity);
  }

  localStorage.setItem("access_token", accessToken);

  return { ...decoded, expiration };
}
